<template>
    <tr v-if="isLoading || !element || !zone" class="order">
        <td colspan="7" class="text-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
            <p>Loading...</p>
        </td>
    </tr>
    <tr v-else-if="element && zone" class="order" :class="{'has-changes': hasChanges, [className(element.status)]: 1, [className(element.property_type)]: 1, 'notice':isEmergency(element)}" @focusin="changed = true">
        <td class="icon">
            <i class="fa" :class="propertyTypeIcon" aria-hidden="true"></i>
        </td>

        <td class="order-date align-top">
            <div>
                <a :href="detailURL" @click="edit" v-text="`Order #${element.id}`"></a>
            </div>
            <div>
                Batch #:
                <input v-if="isAdministrator" type="text" class="form-control form-control-sm inline" v-model.number="element.batch_number">
                <span v-else v-text="element.batch_number"></span>
            </div>
        </td>

        <td class="dates align-top">
            <div>
                Due: <strong v-text="shortDate(element.due_date)"></strong>
            </div>
            <div class="text-nowrap" v-text="orderDateText"></div>
        </td>

        <td class="align-top">
            <div v-text="element.customer_name"></div>
            <div v-if="!dealer || element.viewed_at">
                <div v-text="element.street"></div>
                <div v-if="element.zip_code"
                v-text="`${element.zip_code.city}, ${element.zip_code.state.name} ${element.zip_code.zip_code}`"></div>
            </div>
            <div v-if="element.company && !dealer" v-text="'Dealer: ' + element.company.name"></div>
            <a v-if="!dealer || element.viewed_at" :href="detailURL" @click="edit" class="mt-auto">View Order Details →</a>
        </td>

        <td v-if="dealer && !element.viewed_at" colspan="3">
            <a :href="detailURL" @click="edit">View Order Details →</a>
            <p class="help">You must view the order details before making changes in the list view.</p>
        </td>
        <template v-else>
            <td class="order-breakdown align-bottom">
                <div class="d-flex text-nowrap text-muted">
                    Gallons Ordered:
                    <span class="value pl-1 ml-auto" v-text="element.gallons || 0"></span>
                </div>
                <div class="d-flex text-nowrap mt-1">
                    Gallons Delivered:
                    <span v-if="isEditable" class="pl-1 ml-auto">
                        <input class="form-control form-control-sm inline font-weight-bold"
                                @change="checkOverLimit"
                                v-model.number="element.gallons_delivered"
                                :class="{'is-invalid': validationErrors['gallons_delivered'], 'font-weight-bold': element.gallons_delivered > 0}" type="text" />
                    </span>
                    <span v-else class="pl-1 ml-auto font-weight-bold" v-text="element.gallons_delivered"></span>
                </div>
                <div v-if="validationErrors['gallons_delivered']" v-text="validationErrors['gallons_delivered']" class="validation-msg"></div>
                <div class="d-flex text-nowrap mt-1">
                    Price per Gallon:
                    <span v-if="isAdministrator" class="pl-1 ml-auto">
                        <vue-numeric currency="$" class="form-control form-control-sm inline" v-model="element.price" :class="{'is-invalid':validationErrors['price']}" :precision="3" />
                    </span>
                    <span class="pl-1 ml-auto" v-else v-text="usd3(element.price)"></span>
                </div>
                <div class="d-flex subtotal text-nowrap mt-1">
                    Subtotal:
                    <span class="value pl-1 ml-auto" v-text="usd(oilSubTotal)"></span>
                </div>
            </td>

            <td v-if="isEditable" class="fees align-bottom">
                <div v-if="!emergencyAvailable">
                    Emergency service not available.
                </div>
                <div v-else-if="isEditable" class="d-flex text-nowrap mt-1" :class="{'text-muted': !element.emergency_fee}" :title="emergencyAvailableToolTip">
                    <input class="fee-checkbox" @click="toggleEmergencyFee" type="checkbox" :checked="element.emergency_fee" :disabled="!emergencyAvailable">
                    <span class="fee-label" @click="toggleEmergencyFee">Emergency Fee:</span>
                    <span class="value pl-1 ml-auto" v-text="usd(element.emergency_fee)"></span>
                </div>
                <div v-else class="d-flex text-nowrap mt-1" :class="{'text-muted': !element.emergency_fee}">
                    Emergency Fee: <span class="value pl-1 ml-auto" v-text="usd(element.emergency_fee)"></span>
                </div>

                <div v-if="isEditable" class="d-flex text-nowrap mt-1" :class="{'text-muted': !element.small_order_fee}" :title="orderFeeToolTip">
                    <input class="fee-checkbox" @click="toggleSmallOrderFee" type="checkbox" :checked="element.small_order_fee" :disabled="!orderFeeApplicable" >
                    <span class="fee-label" @click="toggleSmallOrderFee">Small Order Fee:</span>
                    <span class="value pl-1 ml-auto" v-text="usd(element.small_order_fee)"></span>
                </div>
                <div v-else class="d-flex text-nowrap mt-1" :class="{'text-muted': !element.orderFeeApplicable}">
                    Small Order Fee: <span class="value pl-1 ml-auto" v-text="usd(element.small_order_fee)"></span>
                </div>

                <div v-if="isEditable" class="d-flex text-nowrap mt-1" :class="{'text-muted': !element.prime_fee}">
                    <input class="fee-checkbox" @click="togglePrimeFee" type="checkbox" :checked="element.prime_fee">
                    <span class="fee-label" @click="togglePrimeFee">Prime Fee:</span>
                    <span class="value pl-1 ml-auto" v-text="usd(element.prime_fee)"></span>
                </div>
                <div v-else class="d-flex text-nowrap mt-1" :class="{'text-muted': !element.prime_fee}">
                    Prime Fee: <span class="value pl-1 ml-auto" v-text="usd(element.prime_fee)"></span>
                </div>

                <div class="d-flex text-nowrap mt-1" v-if="element.additional_fee">
                    Additional: <span class="value pl-1 ml-auto" v-text="usd(element.additional_fee)"></span>
                </div>
                <div class="d-flex text-nowrap mt-1 subtotal">
                    <strong>Grand Total:</strong>
                    <span class="pl-1 ml-auto font-weight-bold" v-text="usd(total)"></span>
                </div>
            </td>
            <td v-else class="fees align-top">
                <div class="d-flex text-nowrap mt-1">
                    Emergency Fee: <span class="value pl-1 ml-auto" v-text="usd(element.emergency_fee)"></span>
                </div>
                <div class="d-flex text-nowrap mt-1">
                    Small Order Fee: <span class="value pl-1 ml-auto" v-text="usd(element.small_order_fee)"></span>
                </div>
                <div class="d-flex text-nowrap mt-1">
                    Prime Fee: <span class="value pl-1 ml-auto" v-text="usd(element.prime_fee)"></span>
                </div>
                <div class="d-flex text-nowrap mt-1" v-if="element.additional_fee">
                    Additional: <span class="value pl-1 ml-auto" v-text="usd(element.additional_fee)"></span>
                </div>
                <div class="d-flex text-nowrap mt-1 subtotal">
                    <strong>Grand Total:</strong>
                    <span class="pl-1 ml-auto font-weight-bold" v-text="usd(total)"></span>
                </div>
            </td>

            <td v-if="isEditable" class="status align-top">
                <div>
                    <select v-if="isAdministrator" v-model="element.status">
                        <option v-for="value in element.valid_statuses" :value="value" v-text="value"></option>
                    </select>
                    <strong v-else v-text="element.status"></strong>
                </div>
                <div class="mt-2">
                    <span>Delivered on:</span>
                    <datepicker input-class="form-control mt-1" v-model="element.delivered_at" format="M/d/yyyy" :class="{ 'is-invalid':validationErrors['delivered_at'] }" :clear-button="true" />
                </div>
                <div>
                    <span v-if="element.viewed_at" v-text="'Viewed ' + shortDate(element.viewed_at)"></span>
                </div>
            </td>
            <td v-else class="status align-top">
                <div>
                    <strong v-text="element.status"></strong>
                </div>
                <div>
                    <span v-if="element.delivered_at" v-text="'on ' + shortDate(element.delivered_at)"></span>
                </div>
                <div>
                    <span v-if="element.viewed_at" v-text="'Viewed on ' + shortDate(element.viewed_at)"></span>
                </div>
            </td>
        </template>
    </tr>
</template>
<script>
import Datepicker from 'vuejs-datepicker';
import Order from './Order';
import VueNumeric from 'vue-numeric';
import moment from 'moment';
import numeral from 'numeral';

export default {
    props: ['dealer'],
    mixins: [Order],

    components: {
        Datepicker,
        VueNumeric,
    },

    data: () => ({
        validationErrors: {},
        changed: false
    }),

    methods: {
        className(value) {
            if (!value || value == 'undefined') {
                return 'undefined';
            }
            return value.replace(' ', '-').toLowerCase();
        },

        isEmergency(element) {
            if (this.isEditable) {
                return (element.delivery_method == 'Emergency') ? 1 : 0;
            }

            return 0;
        },


        edit(event) {
            if (event) {
                event.preventDefault();
            }

            this.$router.push({name: 'order-detail', params: {id: this.element.id}});
        },

        validate() {
            this.validationErrors = {};

            if (this.element.status == 'Delivered') {
                if (!this.element.price || this.element.price == 0) {
                    this.validationErrors.price = 'Price/Gallon is required';
                }
                if (this.element.gallons_delivered == 0) {
                    this.validationErrors.gallons_delivered = 'Gallons Delivered is required';
                }
                if (!this.element.delivered_at) {
                    this.validationErrors.delivered_at = 'Delivery Date is required';
                }
            }

            if (this.element.gallons_delivered < 0) {
                this.validationErrors.gallons_delivered = 'Gallons delivered cannot be less than zero';
            }
        },

        checkOverLimit() {
            if (!this.isAdministrator && this.element.gallons_delivered > this.element.gallons) {
                this.element.gallons_delivered = this.element.gallons;
            }
        },
        forceZoneReload() {
            // Force zone to be loaded
            if (this.element) {
                const tmp = this.element.dealer_zone_id;
                this.element.dealer_zone_id = 0;
                this.element.dealer_zone_id = tmp;
            }
        }
    },

    computed: {
        hasChanges() {
            return JSON.stringify(this.unchangedRecord) != JSON.stringify(this.element);
        },

        isEditable() {
            if (this.isAdministrator) {
                if (this.element.gallons_delivered == 0) {
                    return true;
                }
                if (!this.element.delivered_at) {
                    return true;
                }
            }

            return !this.element.batch_number;
        },

        detailURL() {
            return `/order/admin/orders/${this.element.id}`;
        },

        orderDateText() {
            return 'Ordered: ' + moment(this.element.created_at).format('M/D h:mm a');
        },

        propertyTypeIcon()
        {
            return this.element.property_type == 'Commercial' ? 'fa-building-o' : 'fa-home';
        },
    },

    mounted() {
        this.forceZoneReload();
    },

    watch: {
        "element.price"() {
            this.validate();
        },

        "element.gallons_delivered"(newVal, oldVal) {
            if (oldVal === undefined || oldVal === null) {
                return;
            }

            if (newVal && !oldVal && (this.element.status == 'New' || this.element.status == 'Processing')) {
                this.localRecord.status = 'Delivered';
            } else if (oldVal && !newVal && (this.element.status == 'Delivered')) {

                if (this.element.batch_number != '') {
                    // Prevent resetting batched orders
                    this.element.gallons_delivered = oldVal;
                } else {

                }
                this.localRecord.status = 'Processing';
                this.localRecord.delivered_at = null;
            }

            this.updateSmallOrderFee();
            this.validate();

            if (!this.isAdministrator && newVal > this.element.gallons
                && this.unchangedRecord.gallons_delivered <= this.element.gallons)
            {
                this.validationErrors.gallons_delivered = 'Contact admin if delivery exceeded order';
            }
        },

        "element.delivered_at"() {
            this.validate();
        },

        "element.status"() {
            this.validate();
        },

        "element.batch_number"() {
            if (this.element && this.element.batch_number && this.element.status != 'Delivered') {
                this.element.status = 'Delivered';
            }
        },
    },
}
</script>
